import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "./ThemeContext";
import { AuthProvider, useAuth } from "./AuthContext";
import { AdminAuthProvider } from "./admin/AdminAuthContext";
import AdminGuard from "./admin/AdminGuard";
import AdminLayout from "./admin/AdminLayout";
import Layout from "./components/Layout";
import WelcomePage from "./pages/WelcomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import CompleteProfilePage from "./pages/CompleteProfilePage";
import HomePage from "./pages/HomePage";
import SearchPage from "./pages/SearchPage";
import WishlistPage from "./pages/WishlistPage";
import ProfilePage from "./pages/ProfilePage";
import EventDetailPage from "./pages/EventDetailPage";
import EnquiryFormPage from "./pages/EnquiryFormPage";
import MyEnquiriesPage from "./pages/MyEnquiriesPage";
import NotificationsPage from "./pages/NotificationsPage";
import MapPage from "./pages/MapPage";
import OrgDashboardPage from "./pages/OrgDashboardPage";
import OrgEventsPage from "./pages/OrgEventsPage";
import OrgEventDetailPage from "./pages/OrgEventDetailPage";
import OrgEventFormPage from "./pages/OrgEventFormPage";
import OrgEnquiriesPage from "./pages/OrgEnquiriesPage";
import OrgProfilePage from "./pages/OrgProfilePage";
import OrgApplyPage from "./pages/OrgApplyPage";
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminOrganizersPage from "./pages/admin/AdminOrganizersPage";
import AdminEventsPage from "./pages/admin/AdminEventsPage";
import AdminEnquiriesPage from "./pages/admin/AdminEnquiriesPage";
import AdminReviewsPage from "./pages/admin/AdminReviewsPage";
import AdminBannersPage from "./pages/admin/AdminBannersPage";
import AdminMastersPage from "./pages/admin/AdminMastersPage";
import AdminReportsPage from "./pages/admin/AdminReportsPage";
import AdminLogsPage from "./pages/admin/AdminLogsPage";
import AdminSettingsPage from "./pages/admin/AdminSettingsPage";
import AdminUserPrivilegesPage from "./pages/admin/AdminUserPrivilegesPage";
import { Toaster } from "sonner";
import "@/App.css";

function ProtectedRoute({ children }) {
  const { isAuthed, loading } = useAuth();
  if (loading) return null;
  if (!isAuthed) return <Navigate to="/login" replace />;
  return children;
}

/** Redirects authed users missing date_of_birth to /complete-profile. */
function NeedsDobGuard({ children }) {
  const { user, isAuthed, loading } = useAuth();
  if (loading) return null;
  if (isAuthed && user && !user.date_of_birth) return <Navigate to="/complete-profile" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" richColors closeButton />
        <Routes>
          {/* Auth routes (no layout) */}
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/complete-profile" element={<ProtectedRoute><CompleteProfilePage /></ProtectedRoute>} />

          {/* Main app with layout */}
          <Route element={<NeedsDobGuard><Layout /></NeedsDobGuard>}>
            <Route path="/" element={<HomePage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/wishlist" element={<WishlistPage />} />
            <Route path="/map" element={<MapPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/event/:id" element={<EventDetailPage />} />
            <Route path="/enquiry/:eventId" element={<ProtectedRoute><EnquiryFormPage /></ProtectedRoute>} />
            <Route path="/enquiries" element={<ProtectedRoute><MyEnquiriesPage /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
            <Route path="/organizer/apply" element={<ProtectedRoute><OrgApplyPage /></ProtectedRoute>} />
            <Route path="/organizer/dashboard" element={<ProtectedRoute><OrgDashboardPage /></ProtectedRoute>} />
            <Route path="/organizer/events" element={<ProtectedRoute><OrgEventsPage /></ProtectedRoute>} />
            <Route path="/organizer/events/new" element={<ProtectedRoute><OrgEventFormPage /></ProtectedRoute>} />
            <Route path="/organizer/events/:id" element={<ProtectedRoute><OrgEventDetailPage /></ProtectedRoute>} />
            <Route path="/organizer/events/:id/edit" element={<ProtectedRoute><OrgEventFormPage /></ProtectedRoute>} />
            <Route path="/organizer/enquiries" element={<ProtectedRoute><OrgEnquiriesPage /></ProtectedRoute>} />
            <Route path="/organizer/profile" element={<ProtectedRoute><OrgProfilePage /></ProtectedRoute>} />
          </Route>

          {/* Admin Portal — separate auth context */}
          <Route path="/admin/*" element={
            <AdminAuthProvider>
              <Routes>
                <Route path="login" element={<AdminLoginPage />} />
                <Route element={<AdminGuard><AdminLayout /></AdminGuard>}>
                  <Route index element={<AdminDashboardPage />} />
                  <Route path="users" element={<AdminUsersPage />} />
                  <Route path="organizers" element={<AdminOrganizersPage />} />
                  <Route path="events" element={<AdminEventsPage />} />
                  <Route path="enquiries" element={<AdminEnquiriesPage />} />
                  <Route path="reviews" element={<AdminReviewsPage />} />
                  <Route path="banners" element={<AdminBannersPage />} />
                  <Route path="masters" element={<AdminMastersPage />} />
                  <Route path="reports" element={<AdminReportsPage />} />
                  <Route path="logs" element={<AdminLogsPage />} />
                  <Route path="settings" element={<AdminSettingsPage />} />
                  <Route path="user-privileges" element={<AdminUserPrivilegesPage />} />
                </Route>
              </Routes>
            </AdminAuthProvider>
          } />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
